/**
 * Discipline SEO Mapping
 * 
 * Maps app discipline names (camelCase) to Strapi SEO page slugs (localized per language).
 * 
 * Why this is needed:
 * - App stores disciplines as camelCase: "trailRunning", "skiTouring", etc.
 * - App translates them: "Trail running" (EN), "Trailrunning" (DE)
 * - App creates slug from translation: createSlug("Trail running") = "trail-running"
 * - BUT Strapi uses different slugs in some cases (e.g., "trail" in French, not "trail-running")
 * 
 * This mapping ensures breadcrumb links match actual Strapi SEO page URLs.
 */

const DISCIPLINE_DATA = {
  // ============================================================================
  // HIKING & WALKING
  // ============================================================================
  
  hiking: {
    seoSlugs: {
      en: 'hiking',
      de: 'wandern',
      fr: 'randonnee',
      it: 'escursionismo'
    },
    appName: 'hiking',
    translationKey: 'General.hiking',
    hasSeoPage: true
  },
  
  mountainHiking: {
    seoSlugs: {
      en: 'hiking',
      de: 'wandern',
      fr: 'randonnee',
      it: 'escursionismo'
    },
    appName: 'mountainHiking',
    translationKey: 'General.mountainHiking',
    hasSeoPage: true,
    note: 'Maps to same SEO page as hiking'
  },
  
  guidedWalking: {
    seoSlugs: {
      en: 'guided-walking',
      de: 'gefuehrte-wanderung',
      fr: 'randonnee-guidee',
      it: 'passeggiata-guidata'
    },
    appName: 'guidedWalking',
    translationKey: 'General.guidedWalking',
    hasSeoPage: true
  },
  
  culinaryTasting: {
    seoSlugs: {
      en: 'food-and-wine-walking',
      de: 'kulinarische-weinwanderung',
      fr: 'balade-enogastronomique',
      it: 'passeggiata-enogastronomica'
    },
    appName: 'culinaryTasting',
    translationKey: 'General.culinaryTasting',
    hasSeoPage: true
  },
  
  snowShoeing: {
    seoSlugs: {
      en: 'snowshoeing',
      de: 'schneeschuhwandern',
      fr: 'raquettes-neige',
      it: 'ciaspolata'
    },
    appName: 'snowShoeing',
    translationKey: 'General.snowShoeing',
    hasSeoPage: true
  },
  
  // ============================================================================
  // RUNNING
  // ============================================================================
  
  running: {
    seoSlugs: {
      en: 'trail-running',
      de: 'trailrunning',
      fr: 'trail',
      it: 'trail-running'
    },
    appName: 'running',
    translationKey: 'General.running',
    hasSeoPage: true,
    note: 'Running maps to trail running SEO pages'
  },
  
  trailRunning: {
    seoSlugs: {
      en: 'trail-running',
      de: 'trailrunning',
      fr: 'trail',
      it: 'trail-running'
    },
    appName: 'trailRunning',
    translationKey: 'General.trailRunning',
    hasSeoPage: true,
    note: 'French uses "trail" not "trail-running"'
  },
  
  // ============================================================================
  // HORSE RIDING
  // ============================================================================
  
  horseRiding: {
    seoSlugs: {
      en: 'horse-riding',
      de: 'reiten',
      fr: 'equitation',
      it: 'equitazione'
    },
    appName: 'horseRiding',
    translationKey: 'General.horseRiding',
    hasSeoPage: true
  },
  
  // ============================================================================
  // BIKING
  // ============================================================================
  
  mountainBiking: {
    seoSlugs: {
      en: 'mountain-biking',
      de: 'mountainbiken',
      fr: 'vtt',
      it: 'mtb'
    },
    appName: 'mountainBiking',
    translationKey: 'General.mountainBiking',
    hasSeoPage: true
  },
  
  eBiking: {
    seoSlugs: {
      en: 'e-biking',
      de: 'e-biken',
      fr: 'velo-electrique',
      it: 'e-bike'
    },
    appName: 'eBiking',
    translationKey: 'General.eBiking',
    hasSeoPage: true
  },
  
  roadCycling: {
    seoSlugs: {
      en: 'road-cycling',
      de: 'strasenradfahren',
      fr: 'cyclisme-sur-route',
      it: 'ciclismo-su-strada'
    },
    appName: 'roadCycling',
    translationKey: 'General.roadCycling',
    hasSeoPage: true
  },
  
  fatBiking: {
    seoSlugs: {
      en: 'fat-biking',
      de: 'fatbiken',
      fr: 'fat-bike',
      it: 'fat-bike'
    },
    appName: 'fatBiking',
    translationKey: 'General.fatBiking',
    hasSeoPage: true
  },
  
  // ============================================================================
  // CLIMBING
  // ============================================================================
  
  mountaineering: {
    seoSlugs: {
      en: 'mountaineering',
      de: 'bergsteigen',
      fr: 'alpinisme',
      it: 'alpinismo'
    },
    appName: 'mountaineering',
    translationKey: 'General.mountaineering',
    hasSeoPage: true
  },
  
  rockClimbing: {
    seoSlugs: {
      en: 'rock-climbing',
      de: 'klettern',
      fr: 'escalade',
      it: 'arrampicata'
    },
    appName: 'rockClimbing',
    translationKey: 'General.rockClimbing',
    hasSeoPage: true
  },
  
  viaFerrata: {
    seoSlugs: {
      en: 'via-ferrata',
      de: 'klettersteig',
      fr: 'via-ferrata',
      it: 'via-ferrata'
    },
    appName: 'viaFerrata',
    translationKey: 'General.viaFerrata',
    hasSeoPage: true
  },
  
  bouldering: {
    seoSlugs: {
      en: 'bouldering',
      de: 'bouldern',
      fr: 'bloc',
      it: 'boulder'
    },
    appName: 'bouldering',
    translationKey: 'General.bouldering',
    hasSeoPage: true
  },
  
  iceClimbing: {
    seoSlugs: {
      en: 'ice-climbing',
      de: 'eisklettern',
      fr: 'escalade-sur-glace',
      it: 'arrampicata-su-ghiaccio'
    },
    appName: 'iceClimbing',
    translationKey: 'General.iceClimbing',
    hasSeoPage: true
  },
  
  // ============================================================================
  // WATER SPORTS
  // ============================================================================
  
  rafting: {
    seoSlugs: {
      en: 'rafting',
      de: 'rafting',
      fr: 'rafting',
      it: 'rafting'
    },
    appName: 'rafting',
    translationKey: 'General.rafting',
    hasSeoPage: true
  },
  
  canyoning: {
    seoSlugs: {
      en: 'canyoning',
      de: 'canyoning',
      fr: 'canyoning',
      it: 'canyoning'
    },
    appName: 'canyoning',
    translationKey: 'General.canyoning',
    hasSeoPage: true
  },
  
  standUpPaddle: {
    seoSlugs: {
      en: 'stand-up-paddle',
      de: 'stand-up-paddle',
      fr: 'stand-up-paddle',
      it: 'stand-up-paddle'
    },
    appName: 'standUpPaddle',
    translationKey: 'General.standUpPaddle',
    hasSeoPage: true
  },
  
  kayaking: {
    seoSlugs: {
      en: 'kayaking',
      de: 'kajakfahren',
      fr: 'kayak',
      it: 'kayak'
    },
    appName: 'kayaking',
    translationKey: 'General.kayaking',
    hasSeoPage: true
  },
  
  sailing: {
    seoSlugs: {
      en: 'sailing',
      de: 'segeln',
      fr: 'voile',
      it: 'vela'
    },
    appName: 'sailing',
    translationKey: 'General.sailing',
    hasSeoPage: true
  },
  
  // ============================================================================
  // AIR SPORTS
  // ============================================================================
  
  paragliding: {
    seoSlugs: {
      en: 'paragliding',
      de: 'gleitschirmfliegen',
      fr: 'parapente',
      it: 'parapendio'
    },
    appName: 'paragliding',
    translationKey: 'General.paragliding',
    hasSeoPage: true
  },
  
  // ============================================================================
  // WINTER SPORTS - SKIING
  // ============================================================================
  
  alpineSkiing: {
    seoSlugs: {
      en: 'alpine-skiing',
      de: 'skifahren',
      fr: 'ski-alpin',
      it: 'sci-alpino'
    },
    appName: 'alpineSkiing',
    translationKey: 'General.alpineSkiing',
    hasSeoPage: true
  },
  
  freerideSkiing: {
    seoSlugs: {
      en: 'freeride-skiing',
      de: 'freeride-skifahren',
      fr: 'ski-freeride',
      it: 'sci-freeride'
    },
    appName: 'freerideSkiing',
    translationKey: 'General.freerideSkiing',
    hasSeoPage: true
  },
  
  skiTouring: {
    seoSlugs: {
      en: 'ski-touring',
      de: 'skitouren',
      fr: 'ski-de-randonnee',
      it: 'sci-alpinismo'
    },
    appName: 'skiTouring',
    translationKey: 'General.skiTouring',
    hasSeoPage: true
  },
  
  telemarkSkiing: {
    seoSlugs: {
      en: 'telemark-skiing',
      de: 'telemark-skifahren',
      fr: 'telemark-ski',
      it: 'telemark-sci'
    },
    appName: 'telemarkSkiing',
    translationKey: 'General.telemarkSkiing',
    hasSeoPage: true
  },
  
  crossCountrySkiing: {
    seoSlugs: {
      en: 'cross-country-skiing',
      de: 'langlaufen',
      fr: 'ski-de-fond',
      it: 'sci-di-fondo'
    },
    appName: 'crossCountrySkiing',
    translationKey: 'General.crossCountrySkiing',
    hasSeoPage: true
  },
  
  // ============================================================================
  // WINTER SPORTS - SNOWBOARDING
  // ============================================================================
  
  onPisteSnowboarding: {
    seoSlugs: {
      en: 'on-piste-snowboarding',
      de: 'on-piste-snowboarden',
      fr: 'snowboard-sur-piste',
      it: 'snowboard-su-pista'
    },
    appName: 'onPisteSnowboarding',
    translationKey: 'General.onPisteSnowboarding',
    hasSeoPage: true
  },
  
  freerideSnowboarding: {
    seoSlugs: {
      en: 'freeride-snowboarding',
      de: 'freeride-snowboarden',
      fr: 'freeride-snowboard',
      it: 'freeride-snowboard'
    },
    appName: 'freerideSnowboarding',
    translationKey: 'General.freerideSnowboarding',
    hasSeoPage: true
  },
  
  splitBoarding: {
    seoSlugs: {
      en: 'splitboarding',
      de: 'splitboarding',
      fr: 'splitboard',
      it: 'splitboarding'
    },
    appName: 'splitBoarding',
    translationKey: 'General.splitBoarding',
    hasSeoPage: true
  },
  
  // ============================================================================
  // WINTER SPORTS - OTHER
  // ============================================================================
  
  tobogganing: {
    seoSlugs: {
      en: 'tobogganing',
      de: 'rodeln',
      fr: 'luge',
      it: 'slittino'
    },
    appName: 'tobogganing',
    translationKey: 'General.tobogganing',
    hasSeoPage: true
  },
  
  huskySledding: {
    seoSlugs: {
      en: 'husky-sledding',
      de: 'husky-rodeln',
      fr: 'traineau-husky',
      it: 'slitta-ad-husky'
    },
    appName: 'huskySledding',
    translationKey: 'General.huskySledding',
    hasSeoPage: true
  },
  
  avalancheTraining: {
    seoSlugs: {
      en: 'avalanche-training',
      de: 'lawinentraining',
      fr: 'formation-avalanche',
      it: 'addestramento-valanghe'
    },
    appName: 'avalancheTraining',
    translationKey: 'General.avalancheTraining',
    hasSeoPage: true
  },
  
  // ============================================================================
  // OTHER EXPERIENCES
  // ============================================================================
  
  yoga: {
    seoSlugs: {
      en: 'yoga',
      de: 'yoga',
      fr: 'yoga',
      it: 'yoga'
    },
    appName: 'yoga',
    translationKey: 'General.yoga',
    hasSeoPage: true
  },
  
  tennis: {
    seoSlugs: {
      en: 'tennis',
      de: 'tennis',
      fr: 'tennis',
      it: 'tennis'
    },
    appName: 'tennis',
    translationKey: 'General.tennis',
    hasSeoPage: true
  },
  
  padel: {
    seoSlugs: {
      en: 'padel',
      de: 'padel',
      fr: 'padel',
      it: 'padel'
    },
    appName: 'padel',
    translationKey: 'General.padel',
    hasSeoPage: true
  },
  
  northernLights: {
    seoSlugs: {
      en: 'northern-lights',
      de: 'nordlicht',
      fr: 'aurores-boreales',
      it: 'aurora-boreale'
    },
    appName: 'northernLights',
    translationKey: 'General.northernLights',
    hasSeoPage: true
  },
};

// Helper to convert language names to codes
const LANG_NAME_TO_CODE = {
  'english': 'en',
  'german': 'de',
  'french': 'fr',
  'italian': 'it',
  'en': 'en',
  'de': 'de',
  'fr': 'fr',
  'it': 'it'
};

/**
 * Get localized SEO slug for a discipline
 * @param {string} discipline - App discipline name (camelCase, e.g., "trailRunning")
 * @param {string} language - Language code ("en", "de", "fr", "it") or name ("english", "german", etc.)
 * @returns {string|null} - Localized slug or null if not found
 */
export function getDisciplineSeoSlug(discipline, language) {
  if (!discipline) return null;
  
  // Convert language name to code if needed
  const langCode = LANG_NAME_TO_CODE[language?.toLowerCase()] || language;
  if (!langCode) return null;
  
  // Get discipline data
  const data = DISCIPLINE_DATA[discipline];
  if (!data || !data.seoSlugs) return null;
  
  return data.seoSlugs[langCode] || null;
}

/**
 * Check if discipline has a SEO page
 * @param {string} discipline - App discipline name (camelCase)
 * @returns {boolean} - True if SEO page exists
 */
export function hasDisciplineSeoPage(discipline) {
  const data = DISCIPLINE_DATA[discipline];
  return data?.hasSeoPage === true;
}

/**
 * Get complete discipline data (for debugging)
 * @param {string} discipline - App discipline name
 * @returns {object|null} - Complete data object or null
 */
export function getDisciplineData(discipline) {
  return DISCIPLINE_DATA[discipline] || null;
}

// Export the full mapping for reference
export const DISCIPLINE_SEO_MAPPING = DISCIPLINE_DATA;
